import React, { useCallback, useMemo, useRef, useState } from 'react'
import useSWR from 'swr'
import {
  NavItem, NavItemInput, PageRecord, PageTypeInfo,
  createNavItem, deleteNavItem, fetchNavDraftChanges, fetchNavItems,
  fetchPageTypeConfig, fetchPages, publishNav, reorderNavItems, revertNav,
  updateNavItem, uploadNavIcon,
} from '../../services/api'
import LinkRefSelector, {
  EMPTY_LINK_REF, LinkRefValue, linkRefToInput, validateLinkRef,
} from '../../components/merchandising/LinkRefSelector'
import { useWidgetSite } from '../../contexts/WidgetSiteContext'
import { useToast } from '../../contexts/ToastContext'
import { Badge, Banner, Button, Drawer, ImageSlot, Panel, useSpeedbump } from '../../components'
import { DISCARD_CHANGES_PROMPT } from '../../constants/unsavedChanges'
import { useDirtyTracker } from '../../hooks'
import { useDragReorder } from '../../hooks'
import { arrayMove } from '../../utils/arrayMove'
import pageStyles from '../../styles/page.module.scss'
import formStyles from '../../styles/forms.module.scss'
import styles from './NavigationEditor.module.scss'

const ALL_DEVICES = ['desktop', 'mobile', 'tablet', 'tv']

interface FormState {
  id: string | null
  label: string
  iconImageId: string | null
  /** Resolved CDN URL, for the ImageSlot preview only. */
  iconUrl: string | null
  linkRef: LinkRefValue
  deviceTargets: string[]
}

const emptyForm = (): FormState => ({
  id: null,
  label: '',
  iconImageId: null,
  iconUrl: null,
  linkRef: EMPTY_LINK_REF,
  deviceTargets: [...ALL_DEVICES],
})

const formFromItem = (item: NavItem): FormState => ({
  id: item.id,
  label: item.label,
  iconImageId: item.icon_image_id,
  iconUrl: item.icon_url,
  linkRef: {
    page_type: item.page_type,
    page_id: item.page_id || '',
    node_id: item.node_id || '',
    // Nav never offers external links, so this stays empty here.
    external_url: '',
  },
  deviceTargets: [...item.device_targets],
})

const destinationSummary = (item: NavItem, types: PageTypeInfo[], pages: PageRecord[]): string => {
  const pt = types.find(t => t.code === item.page_type)
  const base = pt ? pt.title : item.page_type
  // page_id, node_id and path are mutually exclusive ways to address a page
  // (enforced server-side), so whichever is set names this destination.
  if (item.page_id) {
    const page = pages.find(p => p.id === item.page_id)
    return page ? `${page.name} (${page.path})` : base.toLowerCase()
  }
  return item.node_id ? `${base} · ${item.node_id}` : pt?.path ? `${base} (${pt.path})` : base
}

const NavigationEditor: React.FC = () => {
  const { siteId, siteName, setSite } = useWidgetSite()
  const { toast } = useToast()
  const confirm = useSpeedbump()

  const [form, setForm] = useState<FormState | null>(null)
  const [saving, setSaving] = useState(false)

  const formDirty = useDirtyTracker(() => form)

  const { data: navData, mutate, error } = useSWR(
    siteId ? ['nav-items', siteId] : null,
    () => fetchNavItems(siteId),
    { revalidateOnFocus: false }
  )
  const { data: typeConfig } = useSWR(
    siteId ? ['page-type-config', siteId] : null,
    () => fetchPageTypeConfig(siteId),
    { revalidateOnFocus: false }
  )
  const { data: customPagesData } = useSWR(
    siteId ? ['merch-pages', siteId, 'custom'] : null,
    () => fetchPages(siteId, 'custom'),
    { revalidateOnFocus: false }
  )

  const items: NavItem[] = useMemo(() => navData?.items || [], [navData])
  const draftUpdatedAt = navData?.draft_updated_at ?? ''
  const hasDraft = draftUpdatedAt !== ''
  const types: PageTypeInfo[] = typeConfig?.types || []
  const customPages: PageRecord[] = customPagesData?.pages || []

  const { data: changesData } = useSWR(
    siteId && hasDraft ? ['nav-draft-changes', siteId, draftUpdatedAt] : null,
    () => fetchNavDraftChanges(siteId),
    { revalidateOnFocus: false }
  )

  // Copy before sorting — items is SWR's cached array, and sort mutates.
  const tabItems = useMemo(
    () => [...items].sort((a, b) => a.sort_order - b.sort_order),
    [items]
  )

  const openCreate = () => { formDirty.reset(); setForm(emptyForm()) }
  const openEdit = (item: NavItem) => { formDirty.reset(); setForm(formFromItem(item)) }

  // This drawer isn't a route, so the router blocker can't see it close. All
  // three of the Drawer's exits (×, Escape, overlay) plus Cancel land here.
  const closeForm = async () => {
    if (formDirty.isDirty() && !(await confirm({ ...DISCARD_CHANGES_PROMPT }))) return
    formDirty.reset()
    setForm(null)
  }

  const handleLinkRefChange = (linkRef: LinkRefValue) =>
    setForm(f => (f ? { ...f, linkRef } : f))

  const refresh = async () => { await mutate() }

  const handleMutationError = (err: unknown, fallback: string) => {
    toast(err instanceof Error ? err.message : fallback, 'error')
    void refresh() // 409s mean our draft token is stale — reload state
  }

  const buildInput = (f: FormState): NavItemInput => ({
    label: f.label.trim(),
    icon_image_id: f.iconImageId,
    ...linkRefToInput(f.linkRef, types),
    device_targets: f.deviceTargets,
  })

  const handleSave = async () => {
    if (!form) return
    if (!form.label.trim()) { toast('Label is required', 'error'); return }
    const linkError = validateLinkRef(form.linkRef, types)
    if (linkError) { toast(linkError, 'error'); return }
    if (form.deviceTargets.length === 0) { toast('Select at least one device', 'error'); return }

    setSaving(true)
    try {
      if (form.id) {
        await updateNavItem(form.id, { ...buildInput(form), draft_updated_at: draftUpdatedAt })
        toast('Nav item updated')
      } else {
        await createNavItem({
          ...buildInput(form),
          site_id: siteId,
          // Past the last item, not the item count — publishing a removal
          // leaves a gap, and reusing it would tie with an existing item and
          // land the new one in an arbitrary position.
          sort_order: tabItems.reduce((max, i) => Math.max(max, i.sort_order), -1) + 1,
          draft_updated_at: draftUpdatedAt,
        })
        toast('Nav item added')
      }
      await refresh()
      formDirty.reset()
      setForm(null)
    } catch (err) {
      handleMutationError(err, 'Failed to save nav item')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!form?.id) return
    const ok = await confirm({
      title: 'Remove nav item',
      confirmLabel: 'Remove',
      confirmVariant: 'danger',
      message: `Remove “${form.label}” from the menu? The change publishes with the rest of the draft.`,
    })
    if (!ok) return
    try {
      await deleteNavItem(form.id, draftUpdatedAt)
      toast('Nav item removed')
      await refresh()
      formDirty.reset()
      setForm(null)
    } catch (err) {
      handleMutationError(err, 'Failed to remove nav item')
    }
  }

  // Drag reorders a local copy of the list live; the API commit happens once,
  // on drop. draftOrder overlays the server order until the refresh lands.
  const [draftOrder, setDraftOrder] = useState<NavItem[] | null>(null)
  const draftOrderRef = useRef<NavItem[] | null>(null)
  const displayItems = draftOrder ?? tabItems

  const { dragIndex, handleProps, rowProps } = useDragReorder(
    useCallback((from: number, to: number) => {
      const next = arrayMove(draftOrderRef.current ?? tabItems, from, to)
      draftOrderRef.current = next
      setDraftOrder(next)
    }, [tabItems]),
    { dragImageFromParent: true },
  )

  const commitOrder = async () => {
    const order = draftOrderRef.current
    draftOrderRef.current = null
    if (!order) return
    if (order.every((item, i) => item.id === tabItems[i]?.id)) {
      setDraftOrder(null)
      return
    }
    try {
      await reorderNavItems({
        site_id: siteId,
        order: order.map((item, i) => ({ nav_item_id: item.id, sort_order: i })),
        draft_updated_at: draftUpdatedAt,
      })
      await refresh()
    } catch (err) {
      handleMutationError(err, 'Failed to reorder')
    }
    setDraftOrder(null)
  }

  const dragHandleProps = (index: number) => {
    const hp = handleProps(index)
    return {
      ...hp,
      onDragEnd: (e: React.DragEvent) => {
        hp.onDragEnd(e)
        void commitOrder()
      },
    }
  }

  const handlePublish = async () => {
    const changeCount = changesData?.changes.length
    const ok = await confirm({
      title: 'Publish navigation',
      confirmLabel: 'Publish',
      message: `Publish ${changeCount ?? 'the pending'} menu change${changeCount === 1 ? '' : 's'} to the live site?`,
    })
    if (!ok) return
    try {
      await publishNav({ site_id: siteId, draft_updated_at: draftUpdatedAt })
      toast('Navigation published')
      await refresh()
    } catch (err) {
      handleMutationError(err, 'Failed to publish')
    }
  }

  const handleRevert = async () => {
    const ok = await confirm({
      title: 'Discard draft changes',
      confirmLabel: 'Discard',
      confirmVariant: 'danger',
      message: 'Discard all unpublished menu changes? The live menu is untouched.',
    })
    if (!ok) return
    try {
      await revertNav({ site_id: siteId, draft_updated_at: draftUpdatedAt })
      toast('Draft changes discarded')
      await refresh()
    } catch (err) {
      handleMutationError(err, 'Failed to discard draft')
    }
  }

  const statusBadge = (item: NavItem) => {
    if (item.is_deleted) return <Badge variant="error">Removal pending</Badge>
    if (!item.is_live) return <Badge variant="warning">{item.version === 1 ? 'New' : 'Edited'}</Badge>
    return <Badge variant="success">Live</Badge>
  }

  return (
    <div className={pageStyles.pageContainer}>
      <div className={pageStyles.headerButtons}>
        <Button variant="secondary" size="small" onClick={() => setSite('')}>
          Switch Site
        </Button>
      </div>
      <div className={pageStyles.pageHeader}>
        <h1>Navigation — {siteName}</h1>
      </div>

      {error && (
        <Banner variant="error" message={`Unable to load navigation: ${error.message || 'Connection failed'}`} />
      )}

      {hasDraft && (
        <Panel>
          <div className={styles.draftBar}>
            <div>
              <strong>Unpublished changes</strong>
              {changesData && changesData.changes.length > 0 && (
                <ul className={styles.draftChanges}>
                  {changesData.changes.map((c, i) => (
                    <li key={i}>
                      <Badge variant={c.type === 'removed' ? 'error' : c.type === 'added' ? 'success' : 'warning'}>{c.type}</Badge>
                      {' '}{c.name} <span className={styles.draftChangeMeta}>({c.region}{c.details ? ` — ${c.details}` : ''})</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className={styles.draftActions}>
              <Button variant="secondary" size="small" onClick={handleRevert}>Discard</Button>
              <Button size="small" onClick={handlePublish}>Publish</Button>
            </div>
          </div>
        </Panel>
      )}

      <div className={styles.tabRow}>
        <h2 className={styles.menuTitle}>Menu items</h2>
        <Button size="small" onClick={openCreate}>+ Add Item</Button>
      </div>

      {displayItems.length === 0 && !error && (
        <div className={styles.emptyState}>
          <p>No menu items yet.</p>
        </div>
      )}

      {displayItems.length > 0 && (
        <Panel margin="none">
          <ul className={styles.itemList}>
            {displayItems.map((item, index) => (
              <li
                key={item.id}
                className={`${styles.itemRow} ${item.is_deleted ? styles.itemRowDeleted : ''} ${dragIndex === index ? styles.dragging : ''}`}
                {...rowProps(index)}
              >
                <span
                  className={styles.handle}
                  title="Drag to reorder"
                  aria-label="Drag to reorder"
                  {...dragHandleProps(index)}
                >
                  ⠿
                </span>
                {item.icon_url
                  ? <img src={item.icon_url} alt="" className={styles.iconThumb} />
                  : <div className={styles.iconThumbEmpty} />}
                <div className={styles.itemMain}>
                  <button
                    type="button"
                    className={styles.itemLabel}
                    onClick={() => !item.is_deleted && openEdit(item)}
                    disabled={item.is_deleted}
                  >
                    {item.label}
                  </button>
                  <span className={styles.itemDestination}>
                    {destinationSummary(item, types, customPages)}
                  </span>
                </div>
                <div className={styles.itemMeta}>
                  <span className={styles.deviceList}>
                    {item.device_targets.length === ALL_DEVICES.length
                      ? 'all devices'
                      : item.device_targets.join(', ')}
                  </span>
                  {statusBadge(item)}
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <Drawer
        isOpen={form !== null}
        onClose={closeForm}
        title={form?.id ? `Edit: ${form.label}` : 'New Nav Item'}
        footer={form ? (
          <>
            {form.id && (
              <Button variant="danger" size="small" onClick={handleDelete}>Remove</Button>
            )}
            <div className={styles.footerRight}>
              <Button variant="secondary" onClick={closeForm}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : form.id ? 'Save Changes' : 'Add Item'}
              </Button>
            </div>
          </>
        ) : undefined}
      >
        {form && (
          <div className={formStyles.formField} {...formDirty.touchProps}>
            <div className={formStyles.fieldGroup}>
              <label>Label</label>
              <input
                type="text"
                value={form.label}
                onChange={e => setForm({ ...form, label: e.target.value })}
                placeholder="e.g. Schedule"
              />
            </div>

            <div className={formStyles.fieldGroup}>
              <label>Icon</label>
              <div className={styles.iconSlot}>
                <ImageSlot
                  image={form.iconUrl ? { url: form.iconUrl } : null}
                  aspectRatio="1 / 1"
                  darkBackground
                  hint="Icons are rendered on a dark background in the app."
                  onUpload={async (file) => {
                    const res = await uploadNavIcon(siteId, file, form.label || undefined)
                    setForm(f => f ? { ...f, iconImageId: res.tc_image_id, iconUrl: res.cdn_url } : f)
                  }}
                  onRemove={() => setForm(f => f ? { ...f, iconImageId: null, iconUrl: null } : f)}
                />
              </div>
            </div>

            <LinkRefSelector
              siteId={siteId}
              value={form.linkRef}
              onChange={handleLinkRefChange}
            />

            <div className={formStyles.fieldGroup}>
              <label>Devices</label>
              <div className={styles.deviceChecks}>
                {ALL_DEVICES.map(d => (
                  <label key={d} className={formStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={form.deviceTargets.includes(d)}
                      onChange={e => setForm({
                        ...form,
                        deviceTargets: e.target.checked
                          ? [...form.deviceTargets, d]
                          : form.deviceTargets.filter(x => x !== d),
                      })}
                    />
                    {d}
                  </label>
                ))}
              </div>
            </div>

          </div>
        )}
      </Drawer>
    </div>
  )
}

export default NavigationEditor
